import type { RunFilter, RunSummary } from '@console/types'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatInstant, formatMoney } from '../format.js'
import { useVerdicts, verdictLabel } from '../reference/queries.js'
import { useRuns } from '../runs/queries.js'
import {
  PAGE_SIZE,
  pageFromOffset,
  queryFromSearchParams,
  searchParamsForView,
} from '../runs/url.js'
import type { VerdictsResponse } from '@console/types'
import { verdictStampVariant } from '../runs/verdict.js'
import { useCreateSavedView, useDeleteSavedView, useSavedViews } from '../saved-views/queries.js'
import { AsOfLine, Button, Input, Select, Skeleton, Stamp } from '../ui/index.js'
import tableStyles from '../ui/Table.module.css'

const columnHelper = createColumnHelper<RunSummary>()

/**
 * Built from the served vocabulary rather than declared at module scope, so
 * the Verdict cell can label its stamp with the platform's own word. That
 * matters for one specific reason: `stop` covers both `mismatch` and
 * `refused`, so a fixed per-variant label announces the wrong verdict to a
 * screen reader on one of them.
 */
function buildColumns(verdicts: VerdictsResponse | undefined) {
  return [
    columnHelper.accessor('reference', { header: 'Reference' }),
    columnHelper.accessor('verdict', {
      header: 'Verdict',
      cell: (info) => (
        <Stamp
          variant={verdictStampVariant(info.getValue())}
          label={verdictLabel(verdicts, info.getValue())}
        />
      ),
    }),
    columnHelper.accessor('customer', {
      header: 'Customer',
      cell: (info) => info.getValue() ?? '—',
    }),
    columnHelper.accessor('actionType', { header: 'Type' }),
    // Addendum §H: the redaction rule governs published artifacts, not an
    // authenticated operator's view of their own tenant's business — the
    // amount column that was cut earlier in the project comes back.
    columnHelper.accessor('amount', {
      header: 'Amount',
      cell: (info) => formatMoney(info.getValue()) ?? '—',
    }),
    columnHelper.accessor('occurredAt', {
      header: 'Time',
      // `formatInstant`, not `toLocaleString()` — that rendered the browser's
      // local time with NO label, in a table sitting beside as-of lines that
      // say UTC. Two timezones, one screen, one of them unmarked. `LGH-024`.
      cell: (info) => formatInstant(info.getValue()),
    }),
  ]
}

/**
 * UI spec §3. Filter state lives entirely in the URL (`runs/url.ts`), so it
 * is bookmarkable, shareable, and refresh-safe — reloading this page with
 * the same URL reproduces the same query. Sorting, filtering, and
 * pagination are all server-side (phase plan §0); this page never fetches
 * more than one page and filters in memory.
 */
export function RunsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const query = useMemo(() => queryFromSearchParams(searchParams), [searchParams])
  const verdicts = useVerdicts()
  const columns = useMemo(() => buildColumns(verdicts.data), [verdicts.data])
  const runsQuery = useRuns(query)
  const savedViewsQuery = useSavedViews()
  const createSavedView = useCreateSavedView()
  const deleteSavedView = useDeleteSavedView()

  const [customerInput, setCustomerInput] = useState(query.filter.customer ?? '')

  const table = useReactTable({
    data: runsQuery.data?.rows ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  function applyView(filter: RunFilter, page = 1): void {
    setSearchParams(searchParamsForView(filter, page, query.sort, query.sortDirection))
  }

  function goToPage(page: number): void {
    setSearchParams(searchParamsForView(query.filter, page, query.sort, query.sortDirection))
  }

  function toggleSort(field: string): void {
    const nextDirection = query.sort === field && query.sortDirection === 'asc' ? 'desc' : 'asc'
    setSearchParams(searchParamsForView(query.filter, 1, field, nextDirection))
  }

  async function handleSaveView(): Promise<void> {
    const name = window.prompt('Name this view:')
    if (name === null || name.trim() === '') return
    await createSavedView.mutateAsync({ name: name.trim(), filter: query.filter })
  }

  const currentPage = pageFromOffset(query.offset)
  const totalPages = runsQuery.data
    ? Math.max(1, Math.ceil(runsQuery.data.totalCount / PAGE_SIZE))
    : 1

  return (
    <main style={{ padding: '2rem', maxWidth: '72rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Runs</h1>
        {runsQuery.data && (
          <AsOfLine
            asOf={runsQuery.data.asOf}
            onRefresh={() => {
              void runsQuery.refetch()
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
        <Select
          label="Verdict"
          value={query.filter.verdict ?? ''}
          onChange={(event) => {
            const value = event.target.value
            applyView({
              ...query.filter,
              verdict: value === '' ? undefined : (value as RunFilter['verdict']),
            })
          }}
          /*
           * Built from the SERVED vocabulary, not a list typed here. A verdict
           * added or renamed by the platform reaches this filter without a
           * code change — which is the whole of CON-003 in one expression.
           * "All" is the console's own option: it is not a verdict.
           */
          options={[
            { value: '', label: 'All' },
            ...(verdicts.data?.verdicts ?? []).map((v) => ({ value: v.value, label: v.label })),
          ]}
        />
        <form
          onSubmit={(event) => {
            event.preventDefault()
            applyView({
              ...query.filter,
              customer: customerInput === '' ? undefined : customerInput,
            })
          }}
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}
        >
          <Input
            label="Customer"
            value={customerInput}
            onChange={(event) => {
              setCustomerInput(event.target.value)
            }}
          />
          <Button type="submit" variant="secondary">
            Apply
          </Button>
        </form>
        <Button
          variant="secondary"
          onClick={() => {
            void handleSaveView()
          }}
        >
          Save this view
        </Button>
      </div>

      {savedViewsQuery.data && savedViewsQuery.data.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '0.75rem 0' }}>
          {savedViewsQuery.data.map((view) => {
            const filter = view.filter
            return (
              <span key={view.id} style={{ display: 'inline-flex', gap: '0.25rem' }}>
                {view.filterValid && filter !== null ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      applyView(filter)
                    }}
                  >
                    {view.name}
                  </Button>
                ) : (
                  <span
                    style={{ color: 'var(--muted)', fontStyle: 'italic' }}
                    title="This saved view references a filter that no longer exists in your configuration."
                  >
                    {view.name}
                  </span>
                )}
                <Button
                  variant="secondary"
                  onClick={() => {
                    deleteSavedView.mutate(view.id)
                  }}
                >
                  ×
                </Button>
              </span>
            )
          })}
        </div>
      )}

      {runsQuery.isPending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      )}

      {runsQuery.isError && (
        <p role="alert" style={{ color: 'var(--stop)' }}>
          {runsQuery.error.message}
        </p>
      )}

      {runsQuery.data && runsQuery.data.unreadableRows > 0 && (
        /*
         * A DROPPED ROW NOBODY SHOWS IS A SILENT DROP WEARING A NUMBER.
         *
         * `LGH-011` §1: one row the console cannot read no longer costs the
         * others, but a list that quietly omits what it could not parse looks
         * complete — which is worse than one that blanks, because nothing tells
         * the operator to doubt it. Same reasoning as the tenant list's
         * excluded count.
         */
        <p
          role="status"
          style={{
            fontSize: '0.875rem',
            color: 'var(--ink-soft)',
            background: 'var(--paper-2)',
            borderRadius: 'var(--radius-soft)',
            padding: '0.5rem 0.75rem',
            margin: '0 0 0.75rem',
          }}
        >
          {runsQuery.data.unreadableRows === 1
            ? '1 run could not be read by this console and is not shown. This workspace may be running a newer version of the platform.'
            : `${String(runsQuery.data.unreadableRows)} runs could not be read by this console and are not shown. This workspace may be running a newer version of the platform.`}
        </p>
      )}
      {runsQuery.data && runsQuery.data.rows.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>No runs match these filters.</p>
      )}

      {runsQuery.data && runsQuery.data.rows.length > 0 && (
        <>
          <div className={tableStyles['wrapper']}>
            <table className={tableStyles['table']}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} scope="col">
                        <button
                          type="button"
                          onClick={() => {
                            toggleSort(header.column.id)
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            font: 'inherit',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {query.sort === header.column.id &&
                            (query.sortDirection === 'asc' ? ' ↑' : ' ↓')}
                        </button>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      navigate(`/runs/${row.original.runId}`)
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
            <Button
              variant="secondary"
              disabled={currentPage <= 1}
              onClick={() => {
                goToPage(currentPage - 1)
              }}
            >
              ← Previous
            </Button>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-min-mono)' }}>
              Page {currentPage} of {totalPages} · {runsQuery.data.totalCount} runs
            </span>
            <Button
              variant="secondary"
              disabled={currentPage >= totalPages}
              onClick={() => {
                goToPage(currentPage + 1)
              }}
            >
              Next →
            </Button>
          </div>
        </>
      )}
    </main>
  )
}
